/**
 * OrganizationCard Component
 *
 * Displays an organization with its details and action buttons.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Building2, Users, Calendar, Shield, MoreVertical, Edit, Trash2, UserPlus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Organization, OrganizationMember } from '@/stores/organization/organization-store';
import { api } from '@/services/api/client';
import * as errorUtils from '@/utils/errors';

interface OrganizationCardProps {
  organization: Organization;
  onEdit: () => void;
  onDelete: () => void;
  canGrantInternalPlan?: boolean;
  onInternalPlanGranted?: () => void | Promise<void>;
}

const INVITE_ROLES = new Set(['admin', 'member', 'viewer']);
const INTERNAL_PLAN_TIERS = ['basic', 'standard', 'pro', 'enterprise'] as const;
type InternalPlanTier = (typeof INTERNAL_PLAN_TIERS)[number];
type InternalPlanSelection = InternalPlanTier | '';

function isInviteRole(value: string): value is 'admin' | 'member' | 'viewer' {
  return INVITE_ROLES.has(value);
}

function isInternalPlanTier(value: string): value is InternalPlanTier {
  return INTERNAL_PLAN_TIERS.some((tier) => tier === value);
}

function getInitialInternalPlanTier(organization: Organization): InternalPlanSelection {
  if (organization.billingPlan?.provider !== 'internal') {
    return '';
  }

  const billingPlanType = organization.billingPlan.type;
  return isInternalPlanTier(billingPlanType) ? billingPlanType : '';
}

export const OrganizationCard: React.FC<OrganizationCardProps> = ({
  organization,
  onEdit,
  onDelete,
  canGrantInternalPlan = false,
  onInternalPlanGranted,
}) => {
  const [showMembers, setShowMembers] = useState(false);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Invite member state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [grantingInternalPlan, setGrantingInternalPlan] = useState(false);
  const [removingInternalPlan, setRemovingInternalPlan] = useState(false);
  const [selectedInternalPlan, setSelectedInternalPlan] = useState<InternalPlanSelection>(
    () => getInitialInternalPlanTier(organization),
  );
  const [internalPlanError, setInternalPlanError] = useState<string | null>(null);

  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    description: string;
    destructive?: boolean;
    onConfirm: () => void;
  } | null>(null);

  // Check if this organization is a platform admin org
  const isPlatformAdminOrg = organization.isPlatformAdmin === true;
  const hasInternalPlan = organization.billingPlan?.provider === 'internal';

  useEffect(() => {
    setSelectedInternalPlan(getInitialInternalPlanTier(organization));
  }, [organization]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Fetch members
  const fetchMembers = useCallback(async () => {
    setLoadingMembers(true);
    try {
      const response = await api.get<OrganizationMember[]>(
        `/organizations/${organization.slug}/members`
      );
      setMembers(response.data ?? []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
    } finally {
      setLoadingMembers(false);
    }
  }, [organization.slug]);

  // Fetch members when dialog opens
  const handleViewMembers = useCallback(async () => {
    setShowMembers(true);
    await fetchMembers();
  }, [fetchMembers]);

  // Invite member
  const handleInviteMember = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    setInviteError(null);

    try {
      await api.post(`/organizations/${organization.slug}/members`, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });

      // Reset form and refresh members
      setInviteEmail('');
      setInviteRole('member');
      setShowInviteForm(false);
      await fetchMembers();
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to invite member');
      setInviteError(errorMessage);
    } finally {
      setInviting(false);
    }
  }, [organization.slug, inviteEmail, inviteRole, fetchMembers]);

  // Remove member
  const handleRemoveMember = useCallback((memberId: string, memberName: string) => {
    setConfirmAction({
      title: 'Remove member',
      description: `Are you sure you want to remove ${memberName} from this organization?`,
      destructive: true,
      onConfirm: async () => {
        try {
          await api.delete(`/organizations/${organization.slug}/members/${memberId}`);
          await fetchMembers();
        } catch (err: unknown) {
          const errorMessage =
            errorUtils.getErrorMessageOrFallback(err, 'Failed to remove member');
          setInviteError(errorMessage);
        }
      },
    });
  }, [organization.slug, fetchMembers]);

  const handleGrantInternalPlan = useCallback(() => {
    if (selectedInternalPlan === '') {
      setInternalPlanError('Choose an internal plan before saving.');
      return;
    }

    setConfirmAction({
      title: hasInternalPlan ? 'Update internal plan' : 'Grant internal plan',
      description: `Set "${organization.name}" to the internal ${selectedInternalPlan} plan?`,
      onConfirm: async () => {
        setGrantingInternalPlan(true);
        setInternalPlanError(null);
        try {
          await api.post(`/organizations/${organization.slug}/internal-plan`, {
            tier: selectedInternalPlan,
            reason: `Platform admin internal ${selectedInternalPlan} plan grant`,
          });
          if (onInternalPlanGranted) {
            await onInternalPlanGranted();
          }
        } catch (err: unknown) {
          const errorMessage =
            errorUtils.getErrorMessageOrFallback(err, 'Failed to grant internal plan');
          setInternalPlanError(errorMessage);
        } finally {
          setGrantingInternalPlan(false);
        }
      },
    });
  }, [organization.name, organization.slug, onInternalPlanGranted, selectedInternalPlan, hasInternalPlan]);

  const handleRemoveInternalPlan = useCallback(() => {
    setConfirmAction({
      title: 'Remove internal plan',
      description:
        `Remove the internal plan from "${organization.name}" and return it to free? ` +
        'This resets the default plan budget and clears its direct assignments. ' +
        'Custom vault records are preserved.',
      destructive: true,
      onConfirm: async () => {
        setRemovingInternalPlan(true);
        setInternalPlanError(null);
        try {
          await api.delete(`/organizations/${organization.slug}/internal-plan`);
          if (onInternalPlanGranted) {
            await onInternalPlanGranted();
          }
        } catch (err: unknown) {
          const errorMessage =
            errorUtils.getErrorMessageOrFallback(err, 'Failed to remove internal plan');
          setInternalPlanError(errorMessage);
        } finally {
          setRemovingInternalPlan(false);
        }
      },
    });
  }, [organization.name, organization.slug, onInternalPlanGranted]);

  return (
    <>
      <div className="border border-border rounded-lg p-4 bg-card">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg">{organization.name}</h3>
                {isPlatformAdminOrg && (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 rounded-full">
                    <Shield className="h-3 w-3 text-amber-600" />
                    <span className="text-xs font-medium text-amber-600">Platform Admin</span>
                  </div>
                )}
              </div>
              <div className="text-sm text-muted-foreground">@{organization.slug}</div>
              {organization.description && (
                <p className="text-sm text-muted-foreground mt-1">{organization.description}</p>
              )}
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleViewMembers}>
                <Users className="h-4 w-4 mr-2" />
                View Members
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Metadata */}
        <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>Created {formatDate(organization.createdAt)}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="px-2 py-0.5 rounded-full bg-muted text-xs font-medium">
              {organization.billingPlan?.type || 'free'}
            </span>
            {hasInternalPlan && (
              <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                internal
              </span>
            )}
          </div>
          {organization.membership && (
            <div className="flex items-center gap-1">
              <span className="text-xs">Your role: {organization.membership.role}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-4 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button variant="outline" size="sm" onClick={handleViewMembers}>
            <Users className="h-4 w-4 mr-2" />
            Members
          </Button>
          {canGrantInternalPlan && (
            <div className="flex items-center gap-2">
              <Select
                value={selectedInternalPlan}
                onValueChange={(value) => {
                  if (isInternalPlanTier(value)) {
                    setSelectedInternalPlan(value);
                    setInternalPlanError(null);
                  }
                }}
              >
                <SelectTrigger className="h-9 w-36">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGrantInternalPlan}
                disabled={selectedInternalPlan === '' || grantingInternalPlan || removingInternalPlan}
              >
                <Shield className="h-4 w-4 mr-2" />
                {grantingInternalPlan
                  ? 'Saving...'
                  : hasInternalPlan
                  ? 'Update internal plan'
                  : 'Grant internal plan'}
              </Button>
            </div>
          )}
          {canGrantInternalPlan && hasInternalPlan && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemoveInternalPlan}
              disabled={grantingInternalPlan || removingInternalPlan}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <X className="h-4 w-4 mr-2" />
              {removingInternalPlan ? 'Removing...' : 'Remove internal plan'}
            </Button>
          )}
        </div>
        {internalPlanError && (
          <div className="mt-3 p-2 rounded bg-destructive/10 text-destructive text-sm">
            {internalPlanError}
          </div>
        )}
      </div>

      {/* Members Dialog */}
      <Dialog open={showMembers} onOpenChange={(open) => {
        setShowMembers(open);
        if (!open) {
          setShowInviteForm(false);
          setInviteEmail('');
          setInviteError(null);
        }
      }}>
        <DialogContent variant="light" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Members - {organization.name}</DialogTitle>
          </DialogHeader>

          <div className="px-6 py-4">
            {/* Invite Form */}
            {showInviteForm ? (
              <form onSubmit={handleInviteMember} className="p-4 mb-4 rounded-lg border border-border bg-muted/30">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium">Invite Member</h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowInviteForm(false);
                      setInviteEmail('');
                      setInviteError(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="inviteEmail">Email</Label>
                    <Input
                      id="inviteEmail"
                      type="email"
                      placeholder="user@example.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inviteRole">Role</Label>
                    <Select
                      value={inviteRole}
                      onValueChange={(value) => {
                        if (isInviteRole(value)) {
                          setInviteRole(value);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {inviteError && (
                  <div className="mt-3 p-2 rounded bg-destructive/10 text-destructive text-sm">
                    {inviteError}
                  </div>
                )}
                <div className="mt-3">
                  <Button type="submit" className="w-full" disabled={inviting}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    {inviting ? 'Inviting...' : 'Send invitation'}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="mb-4">
                <Button size="sm" onClick={() => setShowInviteForm(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Invite Member
                </Button>
              </div>
            )}

            {/* Members List */}
            {loadingMembers ? (
              <div className="text-center py-8 text-muted-foreground">Loading members...</div>
            ) : members.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No members found</div>
            ) : (
              <div className="space-y-3">
                {members.map((member) => (
                  <div
                    key={member.userId}
                    className="flex items-center justify-between p-3 rounded-lg border border-border"
                  >
                    <div className="flex items-center gap-3">
                      {member.avatar ? (
                        <img
                          src={member.avatar}
                          alt={member.name}
                          className="h-10 w-10 rounded-full"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-medium text-primary">
                            {member.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{member.name}</div>
                        <div className="text-sm text-muted-foreground">@{member.username}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-1 rounded-full bg-muted text-xs font-medium capitalize">
                        {member.role}
                      </span>
                      {member.status && member.status !== 'active' && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                          member.status === 'pending' || member.status === 'invited'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : member.status === 'suspended'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          {member.status}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {(!member.status || member.status === 'active') ? `Joined ${formatDate(member.joinedAt)}` : 'Pending acceptance'}
                      </span>
                      {member.role !== 'owner' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveMember(member.userId, member.name)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmAction !== null} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmAction?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmAction?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={confirmAction?.destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
              onClick={() => {
                confirmAction?.onConfirm();
                setConfirmAction(null);
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default OrganizationCard;
